import { createTheme, Theme, PaletteMode } from '@mui/material/styles';

// Define common color values
const colors = {
  bossardBlue: '#007ac3',
  bossardBlueLight: '#a1c8ea',
  bossardBlueDark: '#001827',
  bossardPetrol: '#1b6f84',
  bossardPetrolLight: '#c2dcd8',
  bossardWhite: '#FFFFFF',
  darkGray: '#333333',
  lightGray: '#eeeeee',
  mediumGray: '#666666',
  black: '#000000',
};

// Define the light theme palette
const lightPalette = {
  primary: {
    main: colors.bossardBlue,
    light: colors.bossardBlueLight,
    dark: colors.bossardBlueDark,
    contrastText: colors.bossardWhite,
  },
  secondary: {
    main: colors.bossardPetrol,
    light: colors.bossardPetrolLight,
    dark: colors.bossardBlueDark,
    contrastText: colors.bossardWhite,
  },
  background: {
    default: colors.bossardWhite,
    paper: colors.lightGray,
  },
  text: {
    primary: colors.black,
    secondary: colors.mediumGray,
  },
};

// Define the dark theme palette
const darkPalette = {
  primary: {
    main: colors.bossardBlue,
    light: colors.bossardBlueLight,
    dark: colors.bossardBlueDark,
    contrastText: colors.bossardWhite,
  },
  secondary: {
    main: colors.bossardPetrol,
    light: colors.bossardPetrolLight,
    dark: colors.bossardBlueDark,
    contrastText: colors.bossardWhite,
  },
  background: {
    default: colors.darkGray,
    paper: '#444444',
  },
  text: {
    primary: colors.bossardWhite,
    secondary: '#cccccc',
  },
};

// Create a theme instance based on the mode
export const createAppTheme = (mode: PaletteMode): Theme => createTheme({
  palette: {
    mode,
    ...(mode === 'light' ? lightPalette : darkPalette),
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    // Make Arial the primary font everywhere; keep fallbacks
    fontFamily: 'Arial, Inter, sans-serif',
    h1: { fontWeight: 800 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    button: { textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          fontFamily: 'Arial, Inter, sans-serif'
        },      "*::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "*::-webkit-scrollbar-track": {
          background: mode === 'light' ? "rgba(0, 0, 0, 0.05)" : "transparent",
          borderRadius: "4px",
        },
        "*::-webkit-scrollbar-thumb": {
          background: mode === 'light' 
            ? "rgba(0, 0, 0, 0.2)" 
            : "rgba(255, 255, 255, 0.35)",  // Increased opacity for better visibility in dark mode
          borderRadius: "4px",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          background: mode === 'light' 
            ? "rgba(0, 0, 0, 0.3)"
            : "rgba(255, 255, 255, 0.45)",  // Increased hover opacity as well
        },
        // Enhanced glass effect using Bossard colors
        ".glass-effect": {
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px) saturate(180%)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: "0 4px 30px rgba(0, 24, 39, 0.1)",
        },
        // Frosted glass effect
        ".glass-effect-frosted": {
          background: "rgba(112, 180, 222, 0.08)", // Bossard Blue 40% with low opacity
          backdropFilter: "blur(10px) saturate(180%)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 4px 30px rgba(0, 24, 39, 0.1)",
        },
        // Colored glass variants
        ".glass-effect-blue": {
          background: "rgba(0, 122, 195, 0.1)", // Bossard Blue
          backdropFilter: "blur(10px) saturate(180%)",
          borderRadius: "16px",
          border: "1px solid rgba(0, 122, 195, 0.2)",
          boxShadow: "0 4px 30px rgba(0, 24, 39, 0.1)",
        },
        ".glass-effect-petrol": {
          background: "rgba(27, 111, 132, 0.1)", // Bossard Petrol
          backdropFilter: "blur(10px) saturate(180%)",
          borderRadius: "16px",
          border: "1px solid rgba(27, 111, 132, 0.2)",
          boxShadow: "0 4px 30px rgba(0, 24, 39, 0.1)",
        }
      },
    },
    MuiButton: {
      styleOverrides: {
        // Use a function so we can style based on ownerState (variant/color)
        root: ({ ownerState, theme }: any) => ({
          borderRadius: '8px',
          transition: 'all 0.3s ease-in-out',
          // Contained buttons (especially primary) should use the theme primary colors
          ...(ownerState && ownerState.variant === 'contained'
            ? {
                // If it's a primary contained button, make it clearly primary
                ...(ownerState.color === 'primary'
                  ? {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    }
                  : {}),
                // preserve contained look for other colors
              }
            : {
              // Non-contained variants keep the glass-like styling used previously
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 15px rgba(0, 24, 39, 0.2)',
              },
            }),
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: mode === 'dark' ? '#64b5f6' : '#007AC2',
          textDecoration: 'none',
          textUnderlineOffset: '2px',
          '&:hover': {
            color: mode === 'dark' ? '#90caf9' : '#005A9E',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px) saturate(180%)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 4px 30px rgba(0, 24, 39, 0.1)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px) saturate(180%)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 4px 30px rgba(0, 24, 39, 0.1)",
        },
      },
    },
  },
  shadows: [
    "none",
    "0 2px 4px rgba(0, 24, 39, 0.1)",
    "0 4px 8px rgba(0, 24, 39, 0.12)",
    "0 8px 16px rgba(0, 24, 39, 0.14)",
    "0 12px 24px rgba(0, 24, 39, 0.16)",
    "0 16px 32px rgba(0, 24, 39, 0.18)",
    "0 20px 40px rgba(0, 24, 39, 0.2)",
    "0 24px 48px rgba(0, 24, 39, 0.22)",
    "0 28px 56px rgba(0, 24, 39, 0.24)",
    "0 32px 64px rgba(0, 24, 39, 0.26)",
    "0 36px 72px rgba(0, 24, 39, 0.28)",
    "0 40px 80px rgba(0, 24, 39, 0.3)",
    "0 44px 88px rgba(0, 24, 39, 0.32)",
    "0 48px 96px rgba(0, 24, 39, 0.34)",
    "0 52px 104px rgba(0, 24, 39, 0.36)",
    "0 56px 112px rgba(0, 24, 39, 0.38)",
    "0 60px 120px rgba(0, 24, 39, 0.4)",
    "0 64px 128px rgba(0, 24, 39, 0.42)",
    "0 68px 136px rgba(0, 24, 39, 0.44)",
    "0 72px 144px rgba(0, 24, 39, 0.46)",
    "0 76px 152px rgba(0, 24, 39, 0.48)",
    "0 80px 160px rgba(0, 24, 39, 0.5)",
    "0 84px 168px rgba(0, 24, 39, 0.52)",
    "0 88px 176px rgba(0, 24, 39, 0.54)",
    "0 92px 184px rgba(0, 24, 39, 0.56)"
  ]
});

// Create default theme as light mode
export const defaultTheme = createAppTheme('light');

// Export the theme creation function as default
export default createAppTheme;